<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>會員管理</h1>
    <div class="header-actions">
      <!-- 待審核獸醫師按鈕 - 只在有待審核時顯示 -->
      <button
        v-if="pendingVets.length > 0"
        class="btn-pending-review"
        @click="showPendingVetsModal = true"
      >
        待審核獸醫師
        <span class="notification-badge">{{ pendingVets.length }}</span>
      </button>

      <!-- 新增客戶建檔按鈕 -->
      <button class="btn-primary" @click="showAddModal = true">
        + 新增客戶建檔
      </button>

      <!-- 優惠券派發按鈕 -->
      <button class="btn-coupon" @click="showCouponModal = true">
        優惠券派發
      </button>
    </div>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋姓名、公司、電話、信箱..."
      />
    </div>

    <div class="filter-dropdown">
      <select id="identity-filter" v-model="activeFilter">
        <option v-for="(type, key) in identityTypes" :key="key" :value="key">
          {{ type.label }} ({{ getIdentityCount(key) }})
        </option>
      </select>
    </div>
  </div>

  <!-- 手機版 - 客戶卡片列表 -->
  <div class="customer-list-mobile">
    <div
      v-for="customer in paginatedCustomers"
      :key="customer.id"
      class="customer-card"
    >
      <div class="customer-header">
        <div class="customer-info">
          <div class="name-row">
            <h3>
              {{ customer.companyName || "未提供公司" }}
            </h3>
            <span
              class="type-badge"
              :style="{
                backgroundColor: `${
                  identityTypes[customer.identity]?.color || '#999'
                }20`,
                color: identityTypes[customer.identity]?.color || '#999',
                border: `1px solid ${
                  identityTypes[customer.identity]?.color || '#999'
                }`,
              }"
            >
              {{ identityTypes[customer.identity]?.label || "未知身分" }}
            </span>
          </div>
          <p class="customer-contact">{{ customer.name || "未提供姓名" }}</p>
          <p class="customer-phone">{{ customer.phone }}</p>
        </div>
      </div>

      <!-- 客戶詳細資訊 -->
      <div class="customer-details">
        <!-- 僅客戶建檔顯示完整資訊 -->
        <template v-if="customer.identity === 'fileonly'">
          <div class="detail-row">
            <span class="label">地址:</span>
            <span class="value">{{ customer.address || "未提供" }}</span>
          </div>
          <div class="detail-row">
            <span class="label">公司/醫院名稱:</span>
            <span class="value">{{ customer.companyName || "未提供" }}</span>
          </div>
          <div class="detail-row">
            <span class="label">統編:</span>
            <span class="value">{{ customer.taxId || "未提供" }}</span>
          </div>
          <div class="detail-row">
            <span class="label">職稱:</span>
            <span class="value">{{ customer.jobTitle || "未提供" }}</span>
          </div>
          <div class="detail-row">
            <span class="label">建檔日期</span>
            <span class="value">{{ customer.createdAt }}</span>
          </div>
        </template>

        <!-- 其他身分顯示原有資訊 -->
        <template v-else>
          <div class="detail-row">
            <span class="label">建檔日期</span>
            <span class="value">{{ customer.createdAt }}</span>
          </div>
        </template>
      </div>

      <!-- 常購商品 - 只顯示非"僅客戶建檔"的會員 -->
      <div v-if="customer.identity !== 'fileonly'">
        <div
          class="favorite-products"
          v-if="customer.favoriteProducts.length > 0"
        >
          <h4>常購商品</h4>
          <div class="products-list">
            <div
              v-for="product in customer.favoriteProducts.slice(0, 3)"
              :key="product.id"
              class="product-tag"
            >
              {{ product.name }}
            </div>
          </div>
        </div>

        <!-- ⭐ 沒有訂單紀錄時顯示 -->
        <div class="favorite-products no-data" v-else-if="customer.noOrder">
          <h4>常購商品</h4>
          <div class="no-order-text">該用戶沒有訂單，無法分析</div>
        </div>
      </div>

      <!-- 手機版操作按鈕 -->
      <div class="customer-card-actions-mobile">
        <button
          class="btn-mobile-customer-action"
          @click="viewCustomerDetail(customer)"
        >
          查看詳情
        </button>

        <button
          v-if="customer.identity !== 'fileonly'"
          class="btn-mobile-customer-action"
          @click="openEditModal(customer)"
        >
          編輯
        </button>

        <button
          v-if="customer.identity !== 'fileonly'"
          class="btn-mobile-customer-action"
          :class="{ locked: !customer.isActive }"
          @click="toggleCustomerStatus(customer.id)"
        >
          <img
            :src="
              customer.isActive
                ? require('@/assets/image/icon/unlock.png')
                : require('@/assets/image/icon/lock.png')
            "
            class="mobile-icon"
          />
          {{ customer.isActive ? "啟用" : "停用" }}
        </button>

        <button
          v-if="customer.identity !== 'fileonly'"
          class="btn-mobile-customer-action delete"
          @click="deleteCustomer(customer.id)"
        >
          刪除
        </button>
      </div>

      <!-- 操作按鈕 - 只顯示非"僅客戶建檔"的會員 -->
      <div v-if="customer.identity !== 'fileonly'" class="card-actions">
        <button class="btn-secondary" @click="viewOrderHistory(customer.id)">
          訂單記錄
        </button>
      </div>
    </div>
  </div>

  <!-- 電腦版 - 客戶列表表格 -->
  <div class="customer-table-desktop">
    <div class="table-container">
      <table class="customer-table">
        <thead>
          <tr>
            <th>客戶名稱</th>
            <th>公司名稱</th>
            <th>身分</th>
            <th>聯絡電話</th>
            <th>狀態</th>
            <th style="text-align: center">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="customer in paginatedCustomers"
            :key="customer.id"
            class="table-row"
          >
            <td>
              <span class="customer-link" @click="viewCustomerDetail(customer)">
                {{ customer.name || "未提供" }}
              </span>
            </td>
            <td>{{ customer.companyName || "未提供" }}</td>
            <td>
              <span
                class="type-badge"
                :style="{
                  backgroundColor: `${
                    identityTypes[customer.identity]?.color || '#999'
                  }20`,
                  color: identityTypes[customer.identity]?.color || '#999',
                  border: `1px solid ${
                    identityTypes[customer.identity]?.color || '#999'
                  }`,
                }"
              >
                {{ identityTypes[customer.identity]?.label || "未知身分" }}
              </span>
            </td>
            <td>{{ customer.phone }}</td>
            <td>
              <span
                class="status-badge"
                :class="customer.isActive ? 'active' : 'inactive'"
              >
                {{ customer.isActive ? "✅ 啟用" : "🔒 停用" }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="table-actions">
                <button
                  class="btn-table-action"
                  @click="viewCustomerDetail(customer)"
                  title="查看詳情"
                >
                  <img src="@/assets/image/icon/search.png" class="all-icon" />
                </button>

                <button
                  v-if="customer.identity !== 'fileonly'"
                  class="btn-table-action"
                  @click="openEditModal(customer)"
                  title="編輯"
                >
                  <img src="@/assets/image/icon/edit.png" class="all-icon" />
                </button>

                <button
                  v-if="customer.identity !== 'fileonly'"
                  class="btn-table-action"
                  :class="{ locked: !customer.isActive }"
                  @click="toggleCustomerStatus(customer.id)"
                  :title="customer.isActive ? '停用客戶' : '啟用客戶'"
                >
                  <img
                    :src="
                      customer.isActive
                        ? require('@/assets/image/icon/unlock.png')
                        : require('@/assets/image/icon/lock.png')
                    "
                    class="all-icon"
                  />
                </button>

                <button
                  v-if="customer.identity !== 'fileonly'"
                  class="btn-table-action delete"
                  @click="deleteCustomer(customer.id)"
                  title="刪除"
                >
                  <img src="@/assets/image/icon/trash.png" class="all-icon" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- 查看客戶詳情 Modal -->
  <div class="modal" v-if="showDetailModal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>客戶詳情 - {{ selectedCustomer?.name }}</h2>
        <button class="close-btn" @click="showDetailModal = false">×</button>
      </div>

      <div class="modal-body">
        <!-- 基本資訊 -->
        <div class="detail-section">
          <h3 class="section-title">📋 基本資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">客戶名稱</span>
              <span class="detail-value">{{ selectedCustomer?.name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">身分</span>
              <span class="detail-value">
                <span
                  class="type-badge"
                  :style="{
                    backgroundColor: `${
                      identityTypes[selectedCustomer?.identity]?.color || '#999'
                    }20`,
                    color:
                      identityTypes[selectedCustomer?.identity]?.color ||
                      '#999',
                    border: `1px solid ${
                      identityTypes[selectedCustomer?.identity]?.color || '#999'
                    }`,
                  }"
                >
                  {{
                    identityTypes[selectedCustomer?.identity]?.label ||
                    "未知身分"
                  }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">建檔日期</span>
              <span class="detail-value">{{
                selectedCustomer?.createdAt
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">狀態</span>
              <span
                class="status-badge"
                :class="selectedCustomer?.isActive ? 'active' : 'inactive'"
              >
                {{ selectedCustomer?.isActive ? "✅ 啟用" : "🔒 停用" }}
              </span>
            </div>
          </div>
        </div>

        <!-- 聯絡資訊 -->
        <div class="detail-section">
          <h3 class="section-title">📞 聯絡資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">聯絡電話</span>
              <span class="detail-value">{{ selectedCustomer?.phone }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">聯絡信箱</span>
              <span class="detail-value">{{ selectedCustomer?.email }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">地址</span>
              <span class="detail-value">{{
                selectedCustomer?.address || "未提供"
              }}</span>
            </div>
          </div>
        </div>

        <!-- 公司/醫院資訊 -->
        <div class="detail-section">
          <h3 class="section-title">🏢 公司/醫院資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">公司名稱</span>
              <span class="detail-value">{{
                selectedCustomer?.companyName || "未提供"
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">公司地址</span>
              <span class="detail-value">{{
                selectedCustomer?.companyAddress || "未提供"
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">統一編號</span>
              <span class="detail-value">{{
                selectedCustomer?.taxId || "未提供"
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">職稱</span>
              <span class="detail-value">{{
                selectedCustomer?.jobTitle || "未提供"
              }}</span>
            </div>
          </div>
        </div>

        <!-- 常購商品 -->
        <div
          class="detail-section"
          v-if="
            selectedCustomer?.identity !== 'fileonly' &&
            selectedCustomer?.favoriteProducts?.length > 0
          "
        >
          <h3 class="section-title">🛒 常購商品</h3>
          <div class="products-grid">
            <div
              v-for="product in selectedCustomer?.favoriteProducts"
              :key="product.id"
              class="product-item"
            >
              {{ product.name }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="modal-footer"
        v-if="selectedCustomer?.identity !== 'fileonly'"
      >
        <button class="btn-cancel" @click="showDetailModal = false">
          關閉
        </button>
        <button
          class="btn-secondary"
          @click="viewOrderHistory(selectedCustomer?.id)"
        >
          查看訂單記錄
        </button>
        <button class="btn-submit" @click="openEditModal(selectedCustomer)">
          編輯客戶
        </button>
      </div>
      <div class="modal-footer" v-else>
        <button class="btn-cancel" @click="showDetailModal = false">
          關閉
        </button>
      </div>
    </div>
  </div>

  <!-- 優惠券派發 Modal -->
  <div v-if="showCouponModal" class="modal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>優惠券派發</h2>
        <button class="close-btn" @click="showCouponModal = false">×</button>
      </div>

      <form @submit.prevent="handleCouponDistribution" class="customer-form">
        <!-- 派發方式選擇 -->
        <div class="form-group">
          <label>派發方式 *</label>
          <div class="radio-group">
            <label class="radio-option">
              <input
                type="radio"
                v-model="couponForm.distributionType"
                value="all"
              />
              <span class="radio-text">群發給所有會員</span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                v-model="couponForm.distributionType"
                value="specific"
              />
              <span class="radio-text">指定特定會員</span>
            </label>

            <!-- ✅ 新增：給予對應的權限 -->
            <label class="radio-option">
              <input
                type="radio"
                v-model="couponForm.distributionType"
                value="byRole"
              />
              <span class="radio-text">指定特定身分組</span>
            </label>
          </div>
        </div>

        <!-- 選擇優惠券 -->
        <div class="form-group">
          <label>選擇優惠券 *</label>
          <select v-model="couponForm.selectedCoupon" required>
            <option value="">請選擇優惠券</option>
            <option
              v-for="coupon in availableCoupons"
              :key="coupon.id"
              :value="String(coupon?.Id || coupon?.id)"
            >
              {{ coupon.CouponName }} - {{ coupon.Description }}
            </option>
          </select>
        </div>

        <!-- 指定會員列表 - 只在選擇"指定特定會員"時顯示 -->
        <div
          v-if="couponForm.distributionType === 'specific'"
          class="form-group"
        >
          <label>選擇會員 * (至少選擇一位)</label>
          <div class="member-selection-list">
            <label
              v-for="member in eligibleMembers"
              :key="member.id"
              class="member-checkbox"
            >
              <input
                type="checkbox"
                :value="member.id"
                v-model="couponForm.selectedMembers"
              />
              <div class="member-info-compact">
                <div class="member-details-compact">
                  <div class="member-name-compact">
                    {{
                      member.companyName && member.name
                        ? `${member.companyName}（${member.name}）`
                        : member.companyName || member.name || "未提供"
                    }}
                  </div>
                  <div class="member-identity-compact">
                    {{ identityTypes[member.identity].label }}
                  </div>
                </div>
              </div>
            </label>
          </div>
          <div class="selected-count">
            已選擇 {{ couponForm.selectedMembers.length }} 位會員
          </div>
        </div>

        <!-- 指定特定身分組 - 只在選擇"byRole"時顯示 -->
        <div v-if="couponForm.distributionType === 'byRole'" class="form-group">
          <label>選擇身分組 *</label>
          <select v-model="couponForm.selectedRole" required>
            <option value="">請選擇身分組</option>
            <option
              v-for="(type, key) in identityTypes"
              :key="key"
              :value="key"
            >
              {{ type.label }}
            </option>
          </select>
        </div>

        <div class="form-actions">
          <button type="button" class="btn-cancel" @click="closeCouponModal">
            取消
          </button>
          <button type="submit" class="btn-submit">
            {{
              couponForm.distributionType === "all"
                ? "派發給所有會員"
                : "派發給選定會員"
            }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- 待審核獸醫師列表 Modal -->
  <div v-if="showPendingVetsModal" class="modal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>待審核獸醫師 ({{ pendingVets.length }})</h2>
        <button class="close-btn" @click="showPendingVetsModal = false">
          ×
        </button>
      </div>

      <div class="pending-vets-modal-body">
        <div class="pending-vets-grid">
          <div
            v-for="vet in pendingVets"
            :key="vet.id"
            class="pending-vet-item"
          >
            <div class="vet-card-header">
              <div class="vet-avatar-large">👨‍⚕️</div>
              <div class="vet-basic-info">
                <h4>{{ vet.doctorName }}</h4>
                <p class="hospital-name">{{ vet.hospitalName }}</p>
              </div>
            </div>

            <div class="vet-contact-info">
              <p>📞 {{ vet.phone }}</p>
              <p>✉️ {{ vet.email }}</p>
              <p class="submit-time-badge">⏰ 提交: {{ vet.submittedAt }}</p>
            </div>

            <button class="btn-review-full" @click="openVetReview(vet)">
              審核資料
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 新增客戶 Modal -->
  <div v-if="showAddModal" class="modal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>新增客戶建檔</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>

      <form @submit.prevent="handleAddCustomer" class="customer-form">
        <div class="form-notice">
          ℹ️ 新增客戶僅能建立「僅客戶建檔」身分,其他身分需由客戶自行註冊申請
        </div>

        <div class="form-group">
          <label>姓名 *</label>
          <input
            type="text"
            v-model="formData.name"
            placeholder="請輸入姓名"
            required
          />
        </div>

        <div class="form-group">
          <label>聯絡電話 *</label>
          <input
            type="tel"
            v-model="formData.phone"
            placeholder="請輸入聯絡電話"
            required
          />
        </div>

        <div class="form-group">
          <label>地址</label>
          <input
            type="text"
            v-model="formData.address"
            placeholder="請輸入地址"
          />
        </div>

        <div class="form-group">
          <label>電子信箱</label>
          <input
            type="email"
            v-model="formData.email"
            placeholder="請輸入電子信箱"
          />
        </div>

        <div class="form-group">
          <label>公司/醫院名稱</label>
          <input
            type="text"
            v-model="formData.companyName"
            placeholder="請輸入公司或醫院名稱"
          />
        </div>

        <div class="form-group">
          <label>職稱</label>
          <input
            type="text"
            v-model="formData.jobTitle"
            placeholder="請輸入職稱"
          />
        </div>

        <div class="form-group">
          <label>公司/醫院地址</label>
          <input
            type="text"
            v-model="formData.companyAddress"
            placeholder="請輸入公司或醫院地址"
          />
        </div>

        <div class="form-group">
          <label>統一編號</label>
          <input
            type="text"
            v-model="formData.taxId"
            placeholder="請輸入統一編號"
            maxlength="8"
          />
        </div>

        <div class="form-actions">
          <button type="button" class="btn-cancel" @click="closeModal">
            取消
          </button>
          <button type="submit" class="btn-submit">建立客戶</button>
        </div>
      </form>
    </div>
  </div>

  <!-- 編輯客戶 Modal -->
  <div v-if="showEditModal && selectedCustomer" class="modal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>編輯客戶資料</h2>
        <button class="close-btn" @click="showEditModal = false">×</button>
      </div>

      <form @submit.prevent="handleEditCustomer" class="customer-form">
        <div class="customer-details edit-mode">
          <!-- 根據身分渲染不同欄位 -->
          <component :is="'div'" v-if="selectedCustomer.identity === 'User'">
            <div class="detail-row">
              <span class="label">姓名：</span>
              <input
                type="text"
                v-model="selectedCustomer.name"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">電話：</span>
              <input
                type="text"
                v-model="selectedCustomer.phone"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">地址：</span>
              <input
                type="text"
                v-model="selectedCustomer.address"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">信箱：</span>
              <input
                type="email"
                v-model="selectedCustomer.email"
                class="edit-input"
                readonly
              />
            </div>

            <div class="detail-row">
              <span class="label">身分：</span>
              <select
                v-model="selectedCustomer.identity"
                class="edit-input"
                required
              >
                <option
                  v-for="(type, key) in identityTypes"
                  :key="key"
                  :value="key"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>
            <div class="detail-row">
              <span class="label">公司名稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyName"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">職稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.jobTitle"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">公司地址：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyAddress"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">統編：</span>
              <input
                type="text"
                v-model="selectedCustomer.taxId"
                class="edit-input"
                maxlength="8"
              />
            </div>
          </component>

          <!-- 獸醫師欄位 -->
          <component :is="'div'" v-if="selectedCustomer.identity === 'Doctor'">
            <div class="detail-row">
              <span class="label">醫院名稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyName"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">醫生姓名：</span>
              <input
                type="text"
                v-model="selectedCustomer.name"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">職稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.jobTitle"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">聯絡電話：</span>
              <input
                type="text"
                v-model="selectedCustomer.phone"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">醫院地址：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyAddress"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">醫師證明：</span>
              <span class="value readonly">
                <a
                  :href="selectedCustomer.vetCertificate"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="cert-link"
                >
                  查看證明
                </a>
              </span>
            </div>
            <div class="detail-row">
              <span class="label">電子信箱：</span>
              <input
                type="email"
                v-model="selectedCustomer.email"
                class="edit-input"
                readonly
              />
            </div>

            <div class="detail-row">
              <span class="label">身分：</span>
              <select
                v-model="selectedCustomer.identity"
                class="edit-input"
                required
              >
                <option
                  v-for="(type, key) in identityTypes"
                  :key="key"
                  :value="key"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>

            <div class="detail-row">
              <span class="label">統編：</span>
              <input
                type="text"
                v-model="selectedCustomer.taxId"
                class="edit-input"
                maxlength="8"
              />
            </div>
          </component>

          <!-- 廠商欄位 -->
          <component
            :is="'div'"
            v-if="
              selectedCustomer.identity === 'Supplier' ||
              selectedCustomer.identity === 'DrugStore'
            "
          >
            <div class="detail-row">
              <span class="label">公司名稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyName"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">聯絡人姓名：</span>
              <input
                type="text"
                v-model="selectedCustomer.name"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">職稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.jobTitle"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">公司地址：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyAddress"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">聯絡電話：</span>
              <input
                type="text"
                v-model="selectedCustomer.phone"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">電子信箱：</span>
              <input
                type="email"
                v-model="selectedCustomer.email"
                class="edit-input"
                readonly
              />
            </div>

            <div class="detail-row">
              <span class="label">身分：</span>
              <select
                v-model="selectedCustomer.identity"
                class="edit-input"
                required
              >
                <option
                  v-for="(type, key) in identityTypes"
                  :key="key"
                  :value="key"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>

            <div class="detail-row">
              <span class="label">統編：</span>
              <input
                type="text"
                v-model="selectedCustomer.taxId"
                class="edit-input"
                maxlength="8"
              />
            </div>
          </component>

          <!-- 僅客戶建檔欄位 -->
          <component :is="'div'" v-if="selectedCustomer.identity === 'Note'">
            <div class="detail-row">
              <span class="label">姓名：</span>
              <input
                type="text"
                v-model="selectedCustomer.name"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">電話：</span>
              <input
                type="text"
                v-model="selectedCustomer.phone"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">地址：</span>
              <input
                type="text"
                v-model="selectedCustomer.address"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">電子信箱：</span>
              <input
                type="email"
                v-model="selectedCustomer.email"
                class="edit-input"
                readonly
              />
            </div>
            <div class="detail-row">
              <span class="label">身分：</span>
              <select
                v-model="selectedCustomer.identity"
                class="edit-input"
                required
              >
                <option
                  v-for="(type, key) in identityTypes"
                  :key="key"
                  :value="key"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>
            <div class="detail-row">
              <span class="label">公司/醫院名稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyName"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">職稱：</span>
              <input
                type="text"
                v-model="selectedCustomer.jobTitle"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">公司/醫院地址：</span>
              <input
                type="text"
                v-model="selectedCustomer.companyAddress"
                class="edit-input"
              />
            </div>
            <div class="detail-row">
              <span class="label">統編：</span>
              <input
                type="text"
                v-model="selectedCustomer.taxId"
                class="edit-input"
                maxlength="8"
              />
            </div>
          </component>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn-cancel"
            @click="showEditModal = false"
          >
            取消
          </button>
          <button type="submit" class="btn-submit">儲存變更</button>
        </div>
      </form>
    </div>
  </div>

  <!-- 獸醫師審核 Modal -->
  <div v-if="showVetReviewModal && selectedVet" class="modal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>獸醫師資格審核</h2>
        <button class="close-btn" @click="showVetReviewModal = false">×</button>
      </div>

      <div class="vet-review-body">
        <div class="review-section">
          <h3>申請人資料</h3>
          <div class="review-info">
            <div class="info-row">
              <span class="label">醫院名稱：</span>
              <span class="value">{{ selectedVet.hospitalName }}</span>
            </div>
            <div class="info-row">
              <span class="label">醫生姓名：</span>
              <span class="value">{{ selectedVet.doctorName }}</span>
            </div>
            <div class="info-row">
              <span class="label">職稱：</span>
              <span class="value">{{ selectedVet.jobTitle }}</span>
            </div>
            <div class="info-row">
              <span class="label">聯絡電話：</span>
              <span class="value">{{ selectedVet.phone }}</span>
            </div>
            <div class="info-row">
              <span class="label">醫院地址：</span>
              <span class="value">{{ selectedVet.hospitalAddress }}</span>
            </div>
            <div class="info-row">
              <span class="label">電子信箱：</span>
              <span class="value">{{ selectedVet.email }}</span>
            </div>
            <div class="info-row">
              <span class="label">統一編號：</span>
              <span class="value">{{ selectedVet.taxId }}</span>
            </div>
            <!-- <div class="info-row">
                <span class="label">申請帳號:</span>
                <span class="value">{{ selectedVet.account }}</span>
              </div> -->
            <div class="info-row">
              <span class="label">提交時間：</span>
              <span class="value">{{ selectedVet.submittedAt }}</span>
            </div>
          </div>
        </div>

        <div class="review-section">
          <h3>醫師證明文件</h3>
          <div class="certificate-preview">
            <img
              :src="selectedVet.vetCertificate"
              alt="獸醫師證明"
              class="cert-image"
            />
            <a
              :href="selectedVet.vetCertificate"
              target="_blank"
              rel="noopener noreferrer"
              class="btn-view-full"
            >
              🔍 查看完整圖片
            </a>
          </div>
        </div>

        <div class="review-actions">
          <button class="btn-reject" @click="handleVetReview(false)">
            ❌ 拒絕申請
          </button>
          <button class="btn-approve" @click="handleVetReview(true)">
            ✅ 通過審核
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 喜好分析 Modal -->
  <div v-if="showPreferencesModal && selectedCustomer" class="modal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>
          {{
            customer.identity === "vet"
              ? customer.name
              : customer.identity === "distributor" ||
                customer.identity === "pharmacy"
              ? customer.companyName
              : customer.name
          }}
          - 喜好商品分析
        </h2>
        <button class="close-btn" @click="showPreferencesModal = false">
          ×
        </button>
      </div>

      <div class="preferences-body">
        <div class="stats-section">
          <h3>購買統計</h3>
          <div class="stats-grid">
            <div class="stat-box">
              <div class="stat-number">
                {{ selectedCustomer.orderCount || 0 }}
              </div>
              <div class="stat-label">總訂單數</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">
                NT$ {{ formatNumber(selectedCustomer.totalAmount || 0) }}
              </div>
              <div class="stat-label">累計金額</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">
                {{ selectedCustomer.favoriteProducts?.length || 0 }}
              </div>
              <div class="stat-label">常購品項</div>
            </div>
          </div>
        </div>

        <div class="products-ranking">
          <h3>常購商品排行</h3>
          <div class="ranking-list">
            <div
              v-for="(product, index) in selectedCustomer.favoriteProducts"
              :key="product.id"
              class="ranking-item"
            >
              <span class="rank">{{ index + 1 }}</span>
              <div class="product-info">
                <div class="product-name">{{ product.name }}</div>
                <div class="product-meta">
                  購買次數: {{ product.count }} 次 | 累計數量:
                  {{ product.quantity }} {{ product.unit }}
                </div>
              </div>
              <div class="product-amount">
                NT$ {{ formatNumber(product.amount) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 訂單紀錄 Modal -->
  <div v-if="showOrderHistoryModal" class="modal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>訂單記錄</h2>
        <button class="close-btn" @click="showOrderHistoryModal = false">
          ×
        </button>
      </div>

      <div class="modal-body">
        <!-- 手機/平板版 - 卡片列表 -->
        <div v-if="orderHistory.length > 0" class="order-cards-mobile">
          <div
            v-for="o in orderHistory"
            :key="o.Id"
            class="order-card-modal"
            @click="goToOrderDetail(o.Id)"
          >
            <div class="order-card-header-modal">
              <span class="order-number-modal" @click="goToOrderDetail(o.Id)">
                {{ o.OrderNo }}
              </span>
              <span class="order-amount-modal">NT$ {{ o.TotalAmount }}</span>
            </div>

            <div class="order-card-body-modal">
              <div class="order-info-row-modal">
                <span class="label-modal">項目數</span>
                <span class="value-modal">{{ o.ProductCount }}</span>
              </div>
              <div class="order-info-row-modal">
                <span class="label-modal">付款方式</span>
                <span class="value-modal">{{ o.PaymentMethod }}</span>
              </div>
              <div class="order-info-row-modal">
                <span class="label-modal">訂單狀態</span>
                <span class="value-modal">{{ o.OrderStatus }}</span>
              </div>
              <div class="order-info-row-modal">
                <span class="label-modal">付款狀態</span>
                <span class="value-modal">{{
                  o.PaymentStatus ? "已付款" : "未付款"
                }}</span>
              </div>
              <div class="order-info-row-modal">
                <span class="label-modal">建立日期</span>
                <span class="value-modal">{{ formatDate(o.CreatedDate) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 電腦版 - 表格 -->
        <div v-if="orderHistory.length > 0" class="order-table-desktop-modal">
          <table class="order-table">
            <thead>
              <tr>
                <th>訂單編號</th>
                <th>金額</th>
                <th>項目數</th>
                <th>付款方式</th>
                <th>訂單狀態</th>
                <th>付款狀態</th>
                <th>建立日期</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in orderHistory" :key="o.Id">
                <td>
                  <span class="order-link" @click="goToOrderDetail(o.Id)">
                    {{ o.OrderNo }}
                  </span>
                </td>
                <td>{{ o.TotalAmount }}</td>
                <td>{{ o.ProductCount }}</td>
                <td>{{ o.PaymentMethod }}</td>
                <td>{{ o.OrderStatus }}</td>
                <td>{{ o.PaymentStatus ? "已付款" : "未付款" }}</td>
                <td>{{ formatDate(o.CreatedDate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 當沒有訂單記錄時顯示提示 -->
        <div v-if="orderHistory.length === 0" class="no-orders-message">
          <div class="empty-state">
            <div class="empty-icon">📋</div>
            <h3>尚無訂單記錄</h3>
            <p>此客戶目前還沒有任何訂單記錄</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ✅ 分頁控制區 -->
  <div class="pagination" v-if="filteredCustomers.length > 0">
    <button
      class="btn btn-sm"
      :disabled="currentPage === 1"
      @click="currentPage--"
    >
      ◀ 上一頁
    </button>

    <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 頁</span>

    <button
      class="btn btn-sm"
      :disabled="currentPage === totalPages"
      @click="currentPage++"
    >
      下一頁 ▶
    </button>
  </div>
</template>

<script src="@/assets/js/customer.js"></script>
<style scoped src="@/assets/css/customer.css"></style>
